import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { Template } from '../types';
import { api } from '../api/Api';

export type PhotoTransform = {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotation?: number;
};

const useEditorProviderService = () => {
    const [templates, setTemplates] = useState<Array<Template>>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);

    const [photoTransform, setPhotoTransform] = useState<PhotoTransform | null>(null);

    const [resultFile, setResultFile] = useState<File | null>(null);
    const [clearResultFile, setClearResultFile] = useState<File | null>(null);

    const [resultUrl, setResultUrl] = useState<string | null>(null);
    const [clearResultUrl, setClearResultUrl] = useState<string | null>(null);

    const selectTemplate = useCallback(
        (templateId: string) => {
            const template = templates.find((t) => t === templateId) || null;
            setSelectedTemplate(template);
        },
        [templates],
    );

    const clearPhotoTransform = useCallback(() => {
        setPhotoTransform(null);
    }, []);

    const reset = useCallback(() => {
        setSelectedTemplate(null);
        setPhotoFile(null);
        setPhotoUrl(null);
        setPhotoTransform(null);
        setResultFile(null);
        setClearResultFile(null);
        setResultUrl(null);
    }, []);

    useEffect(() => {
        api.getTemplates().then((data) => {
            setTemplates(data);
        });
    }, []);

    useEffect(() => {
        if (!photoFile) {
            setPhotoUrl(null);
            return;
        }

        const url = URL.createObjectURL(photoFile);
        setPhotoUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [photoFile]);

    useEffect(() => {
        if (!resultFile) {
            setResultUrl(null);
            return;
        }
        const url = URL.createObjectURL(resultFile);
        setResultUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [resultFile]);

    useEffect(() => {
        if (!clearResultFile) {
            setClearResultUrl(null);
            return;
        }
        const url = URL.createObjectURL(clearResultFile);
        setClearResultUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [clearResultFile]);

    return {
        templates,
        selectedTemplate,
        selectTemplate,

        photoFile,
        photoUrl,
        setPhotoFile,
        clearPhoto: () => setPhotoFile(null),

        photoTransform,
        setPhotoTransform,
        clearPhotoTransform,

        resultFile,
        resultUrl,
        setResultFile,
        clearResult: () => setResultFile(null),

        clearResultFile,
        clearResultUrl,
        setClearResultFile,

        reset,
    };
};

export const EditorContext = createContext<ReturnType<typeof useEditorProviderService>>({
    templates: [],
    selectedTemplate: null,
    selectTemplate: () => {},

    photoFile: null,
    photoUrl: null,
    setPhotoFile: () => {},
    clearPhoto: () => {},

    photoTransform: null,
    setPhotoTransform: () => {},
    clearPhotoTransform: () => {},

    resultFile: null,
    resultUrl: null,
    setResultFile: () => {},
    clearResult: () => {},

    clearResultFile: null,
    clearResultUrl: null,
    setClearResultFile: () => {},

    reset: () => {},
});

export const EditorProvider = ({ children }: PropsWithChildren<{}>) => {
    const providerValue = useEditorProviderService();

    return <EditorContext.Provider value={providerValue}>{children}</EditorContext.Provider>;
};

export const useEditor = () => useContext(EditorContext);
